/**
 * A soft fade at the edge of a scroll area that has more beyond it: at the top
 * once scrolled below the top, at the bottom while more lies below. With
 * overlay scrollbars (macOS's default) nothing else says a box scrolls.
 *
 * <p>useEsScrollEdges( ref, mounted ) watches the box and writes data-edge-top
 * and data-edge-bottom on it; mounted is any value that changes when the box
 * comes and goes (the open topic's key, say), so a box drawn after the first
 * render is picked up. The box carries the class esScrollEdges, whose mask
 * fades the content itself, so the fade needs no colour of its own (it works
 * on any ground, light or dark) and takes no clicks.
 *
 * <p>Measured on scroll, on resize (ResizeObserver), and when the content
 * changes (MutationObserver), at most once a frame. Scroll-driven animations
 * would need no script at all, but Firefox still keeps them behind a flag.
 */
import { useEffect } from 'react';
import './EsScrollEdges.css';

/** Within this many pixels of an end counts as at it: sub-pixel scroll positions never quite reach 0. */
const SLACK = 2;

/** Which edges have more beyond them, from a box's scroll geometry. */
export function esScrollEdges( pScrollTop, pScrollHeight, pClientHeight ) {
    return {
        top: pScrollTop > SLACK,
        bottom: pScrollHeight - pScrollTop - pClientHeight > SLACK,
    };
}

export function useEsScrollEdges( pRef, pMounted ) {
    useEffect( () => {
        const aBox = pRef.current;
        if ( !aBox ) return undefined;

        let aFrame = 0;
        const measure = () => {
            aFrame = 0;
            const aEdges = esScrollEdges( aBox.scrollTop, aBox.scrollHeight, aBox.clientHeight );
            aBox.dataset.edgeTop = String( aEdges.top );
            aBox.dataset.edgeBottom = String( aEdges.bottom );
        };
        const schedule = () => {
            if ( !aFrame ) aFrame = requestAnimationFrame( measure );
        };

        measure();
        aBox.addEventListener( 'scroll', schedule, { passive: true } );
        const aResize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver( schedule );
        aResize?.observe( aBox );
        const aContent = typeof MutationObserver === 'undefined' ? null : new MutationObserver( schedule );
        aContent?.observe( aBox, { childList: true, subtree: true, characterData: true } );

        return () => {
            if ( aFrame ) cancelAnimationFrame( aFrame );
            aBox.removeEventListener( 'scroll', schedule );
            aResize?.disconnect();
            aContent?.disconnect();
        };
    }, [ pRef, pMounted ] );
}
